import React, { useState } from 'react';

interface APICall {
  timestamp: string;
  method: string;
  url: string;
  status: number;
  scenario?: string;
  duration?: number;
  requestBody?: any;
  responseBody?: any;
}

interface APIMonitorProps {
  apiCalls: APICall[];
  isMonitoring: boolean;
}

export const APIMonitor: React.FC<APIMonitorProps> = ({
  apiCalls,
  isMonitoring
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCall, setSelectedCall] = useState<APICall | null>(null);

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-600 dark:text-green-400';
    if (status >= 300 && status < 400) return 'text-blue-600 dark:text-blue-400';
    if (status >= 400 && status < 500) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getMethodColor = (method: string) => {
    switch (method.toLowerCase()) {
      case 'get': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'post': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'put': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'delete': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.search;
    } catch {
      return url;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              API Monitor
            </h2>
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${
              isMonitoring 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                isMonitoring ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
              }`} />
              {isMonitoring ? 'Monitoring' : 'Idle'}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {apiCalls.length} calls
            </span>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg 
                className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`transition-all duration-300 ${isExpanded ? 'max-h-96' : 'max-h-48'} overflow-hidden`}>
        <div className="p-6">
          {apiCalls.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 dark:text-gray-500 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {isMonitoring ? 'Waiting for API calls...' : 'No API calls recorded'}
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {apiCalls.slice().reverse().map((call, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedCall(call)}
                >
                  {/* Timestamp */}
                  <div className="text-xs text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">
                    {formatTime(call.timestamp)}
                  </div>

                  {/* Method */}
                  <div className={`px-2 py-1 rounded text-xs font-medium w-16 text-center ${getMethodColor(call.method)}`}>
                    {call.method}
                  </div>

                  {/* Status */}
                  <div className={`text-sm font-medium w-12 ${getStatusColor(call.status)}`}>
                    {call.status}
                  </div>

                  {/* URL */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {formatUrl(call.url)}
                    </div>
                    {call.scenario && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {call.scenario}
                      </div>
                    )}
                  </div>

                  {/* Duration */}
                  {call.duration && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 w-16 text-right">
                      {call.duration}ms
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* API Call Detail Modal */}
      {selectedCall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-700 rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  API Call Details
                </h3>
                <button
                  onClick={() => setSelectedCall(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Request Details */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 dark:text-white">Request</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Method:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getMethodColor(selectedCall.method)}`}>
                        {selectedCall.method}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                      <span className={`font-medium ${getStatusColor(selectedCall.status)}`}>
                        {selectedCall.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Time:</span>
                      <span className="text-gray-900 dark:text-white">
                        {formatTime(selectedCall.timestamp)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">URL:</span>
                      <div className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono break-all">
                        {selectedCall.url}
                      </div>
                    </div>
                  </div>

                  {selectedCall.requestBody && (
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-white mb-2">Request Body</h5>
                      <pre className="p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs overflow-x-auto">
                        {JSON.stringify(selectedCall.requestBody, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>

                {/* Response Details */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 dark:text-white">Response</h4>
                  {selectedCall.responseBody && (
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-white mb-2">Response Body</h5>
                      <pre className="p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs overflow-x-auto max-h-80">
                        {JSON.stringify(selectedCall.responseBody, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};